import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import { State } from 'redux/initialState';
import tableColumns from 'modules/transactions/pages/TransactionsList/utils/tableColumns';
import { operationList } from 'modules/transactions/constants/operationList';
import { getActualPartner, getPartnerOperations } from 'utils/filters';
import { hasPartnersDecoupled } from 'utils/showDetails';
import operationTypes from 'constants/operationTypes';
import { mapOperationsToTable } from '../utils/mapOperationsToTable';

type UseTransactionsListProps = {
  societyId: string;
  partnerId: string;
  isReadOnly: boolean;
  operations: any[];
};

export const excludedOps = [
  operationTypes.ADD_PARTNER,
  operationTypes.ADD_PARTNERS,
  operationTypes.ADD_BENEFICIARY,
  operationTypes.STOCK_OPTION,
  operationTypes.PHANTOM_SHARE,
];

export const useTransactionsList = ({
  societyId,
  partnerId,
  isReadOnly,
  operations,
}: UseTransactionsListProps) => {
  const { i18n } = useTranslate();

  const user = useSelector((state: State) => state?.user);
  const partners = useSelector((state: State) => state.partners);
  const actualSociety = useSelector(
    (state: State) => state?.society?.actualSociety
  );

  const [userOperations] = useState<any[]>();
  const [, setCurrentOperations] = useState<any[]>();

  const [filterBy, setFilterBy] = useState(operationList[0]); // ALL

  const columns = tableColumns(i18n);

  const parsedOperations = useMemo(
    () =>
      mapOperationsToTable({
        operations,
        society: actualSociety,
        isReadOnly,
      }),
    [actualSociety, operations, isReadOnly]
  );

  // TODO: [AXA] Check if this is necessary
  // getUserTransactions(user['_id'], setUserOperations);
  // dispatch(getStockPlans(societyId));
  // dispatch(getHoldingClasses({societyId}));

  const getCurrentUserOperations = () =>
    userOperations?.map((operation) => ({
      operation,
      society: operation?.society,
    })) || [];

  const getCurrentSocietyOperations = () =>
    operations.map((operation: any) => ({
      operation,
      society: actualSociety,
    }));

  const getCurrentPartnerOperations = () => {
    const currentPartners = hasPartnersDecoupled()
      ? partners
      : actualSociety.partners;

    const partner = getActualPartner(currentPartners, partnerId);
    const partnerOperations = getPartnerOperations(partner, actualSociety);
    return partnerOperations.map((operation: any) => ({
      operation,
      society: actualSociety,
    }));
  };

  // TODO: [AXA] This filter should be done in the backend
  const filterAndSortOperations = (transactions: any[]) => {
    const filteredTransactions = transactions
      ?.filter(
        (transaction: any) =>
          !excludedOps.includes(transaction.operation.operationType)
      )
      .sort(
        (transactionA: any, transactionB: any) =>
          new Date(transactionB.operation.date as Date).getTime() -
          new Date(transactionA.operation.date as Date).getTime()
      );
    setCurrentOperations(filteredTransactions);
  };

  useEffect(() => {
    if (user) {
      let transactions;
      if (!societyId) {
        transactions = getCurrentUserOperations();
      } else if (!partnerId) {
        transactions = getCurrentSocietyOperations();
      } else if (actualSociety) {
        transactions = getCurrentPartnerOperations();
      }

      filterAndSortOperations(transactions);
    }
  }, [actualSociety, user, userOperations, societyId, partnerId, partners]); // eslint-disable-line

  return {
    columns,
    tableData: parsedOperations,
    filterBy,
    setFilterBy,
  };
};
